import { useCallback } from 'react';
import { isAdmin as checkAdmin } from '../../api';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Flag,
  BookOpen,
  OctagonX,
  BarChart2,
  Users,
  Settings,
  LogOut,
} from 'lucide-react';
import logoMark from '../../assets/logo-mark.png';
import styles from './SideNav.module.css';

const NAV_ITEMS = [
  { label: 'Dashboard',    icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Calendar',     icon: Calendar,        path: '/calendar' },
  { label: 'Golf courses', icon: Flag,            path: '/golf-courses' },
  { label: 'Bookings',     icon: BookOpen,        path: '/bookings' },
  { label: 'Blocks',       icon: OctagonX,        path: '/blocks' },
  { label: 'Reports',      icon: BarChart2,        path: '/reports' },
  // Admin-only items (filtered below)
  { label: 'Users',        icon: Users,           path: '/users',    adminOnly: true },
  { label: 'Settings',     icon: Settings,        path: '/settings', adminOnly: true },
];

function getInitials(user) {
  if (!user) return '??';
  const name = user.full_name || user.username || '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || '??';
}

function getRoleLabel(role) {
  if (!role) return '';
  if (role.toLowerCase() === 'administrator') return 'Administrator';
  return 'Staff';
}

export default function SideNav({ user, onLogout }) {
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    navigate('/login');
  }, [navigate, onLogout]);

  const isAdmin = checkAdmin(user);

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.adminOnly || isAdmin
  );

  const initials = getInitials(user);
  const displayName = user?.full_name || user?.username || '';
  const roleLabel = getRoleLabel(user?.role);

  return (
    <nav
      className={styles.nav}
      aria-label="Main navigation"
    >
      {/* Brand block */}
      <div className={styles.brand}>
        <img
          src={logoMark}
          alt="Courtesy Point"
          className={styles.logoMark}
        />
        <div className={styles.brandText}>
          <span className={styles.brandName}>CMS Tee Time</span>
        </div>
      </div>

      {/* Nav menu */}
      <ul className={styles.menu}>
        {visibleItems.map(({ label, icon: Icon, path }) => (
          <li key={path}>
            <NavLink
              to={path}
              className={({ isActive }) =>
                `${styles.item}${isActive ? ' ' + styles.active : ''}`
              }
            >
              <span className={styles.itemIcon}>
                <Icon size={20} strokeWidth={1.5} />
              </span>
              <span className={styles.itemLabel}>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.userRow}>
          {/* Avatar */}
          <div
            className={styles.avatar}
            aria-label={`${displayName} — ${roleLabel}`}
          >
            {initials}
          </div>

          {/* User info */}
          <div className={styles.userInfo}>
            <div className={styles.userName}>{displayName}</div>
            <div className={styles.userRole}>{roleLabel}</div>
          </div>

          {/* Logout button */}
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>

      </div>
    </nav>
  );
}
